import { useState, useEffect } from 'react';
import axiosInstance from '../../config/axiosInstance';
import { toast } from 'react-toastify';
import { User } from 'lucide-react';
import AuthenticatedMedia from './AuthenticatedMedia';

const ManageModal = ({ selectedUser, onClose, onUpdate, onChangeLoginPassword, onOpenFundPassword }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedUser, setEditedUser] = useState({ ...selectedUser });
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingSuspend, setLoadingSuspend] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [kycReason, setKycReason] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Update editedUser when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      setEditedUser({ ...selectedUser });
      setHasChanges(false);
    }
  }, [selectedUser]);

  // Check for changes whenever editedUser changes
  useEffect(() => {
    if (selectedUser && editMode) {
      const changed =
        editedUser.fullName !== selectedUser.fullName ||
        editedUser.email !== selectedUser.email ||
        editedUser.phone !== selectedUser.phone ||
        editedUser.dateOfBirth !== selectedUser.dateOfBirth ||
        editedUser.age !== selectedUser.age ||
        editedUser.country !== selectedUser.country ||
        editedUser.address !== selectedUser.address;

      setHasChanges(changed);
    }
  }, [editedUser, selectedUser, editMode]);

  const openEditMode = () => {
    setEditedUser({ ...selectedUser });
    setHasChanges(false);
    setEditMode(true);
  };

  const saveUserProfile = async () => {
    try {
      setLoadingSave(true);
      const response = await axiosInstance.put(
        `/api/admin/users/${selectedUser.id}/profile`,
        {
          fullName: editedUser.fullName,
          email: editedUser.email,
          phone: editedUser.phone,
          dateOfBirth: editedUser.dateOfBirth,
          age: editedUser.age,
          country: editedUser.country,
          address: editedUser.address,
        }
      );

      if (response.data.success) {
        onUpdate(editedUser);
        toast.success('User profile updated successfully!');
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      toast.error('Failed to update user profile. Please try again.');
    } finally {
      setLoadingSave(false);
    }
  };

  const toggleBanStatus = async () => {
    try {
      setLoadingSuspend(true);
      const isSuspended = selectedUser.status === 'SUSPENDED';
      const endpoint = isSuspended
        ? `/api/admin/users/${selectedUser.uid}/unsuspend`
        : `/api/admin/users/${selectedUser.uid}/suspend`;

      const response = await axiosInstance.post(endpoint);

      // Handle successful response
      if (response.data.success || response.status === 200) {
        const newStatus = isSuspended ? 'ACTIVE' : 'SUSPENDED';
        onUpdate({ ...selectedUser, status: newStatus });
        toast.success(
          `User ${isSuspended ? 'unsuspended' : 'suspended'} successfully!`
        );
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to update user status.');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      const errorMessage =
        error.response?.data?.message ||
        'Failed to update user status. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoadingSuspend(false);
    }
  };

  const approveKYC = async () => {
    try {
      setLoadingApprove(true);
      const response = await axiosInstance.post(
        '/api/admin/kyc/update-status',
        {
          userId: selectedUser.id,
          status: 'APPROVED',
          reason: kycReason || 'All documents verified and valid',
        }
      );

      if (response.data.success) {
        onUpdate({ ...selectedUser, kycStatus: 'APPROVED' });
        toast.success('KYC Approved successfully!');
        setKycReason('');
        onClose();
      }
    } catch (error) {
      console.error('Error approving KYC:', error);
      toast.error('Failed to approve KYC. Please try again.');
    } finally {
      setLoadingApprove(false);
    }
  };

  const rejectKYC = async () => {
    try {
      setLoadingReject(true);
      const response = await axiosInstance.post(
        '/api/admin/kyc/update-status',
        {
          userId: selectedUser.id,
          status: 'REJECTED',
          reason: kycReason || 'Documents verification failed',
        }
      );

      if (response.data.success) {
        onUpdate({ ...selectedUser, kycStatus: 'REJECTED' });
        toast.success('KYC Rejected successfully!');
        setKycReason('');
        onClose();
      }
    } catch (error) {
      console.error('Error rejecting KYC:', error);
      toast.error('Failed to reject KYC. Please try again.');
    } finally {
      setLoadingReject(false);
    }
  };

  if (!selectedUser) return null;

  return (
    <div
      className='modal d-block'
      tabIndex='-1'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className='modal-dialog modal-lg modal-dialog-centered'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-content border-0 shadow-lg'>
          {/* Header */}
          <div
            className='modal-header text-white border-0'
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <h5 className='modal-title d-flex align-items-center gap-2'>
              <User size={24} />
              {editMode ? 'Edit User Profile' : 'Manage User'}
            </h5>
            <button
              type='button'
              className='btn-close btn-close-white'
              onClick={onClose}
            ></button>
          </div>

          {/* Body */}
          <div className='modal-body p-4'>
            {!editMode ? (
              // View Mode
              <>
                {/* User Info Card */}
                <div className='card bg-light border-0 mb-4'>
                  <div className='card-body'>
                    <h6 className='card-title fw-bold mb-3'>
                      Basic Information
                    </h6>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>User ID</small>
                        <strong>{selectedUser.id}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Email</small>
                        <strong>{selectedUser.email}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Full Name</small>
                        <strong>{selectedUser.fullName || 'N/A'}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Phone</small>
                        <strong>{selectedUser.phone || 'N/A'}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Country</small>
                        <strong>{selectedUser.country || 'N/A'}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Join Date</small>
                        <strong>
                          {new Date(selectedUser.createdAt).toLocaleString()}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Status Card */}
                <div className='card bg-light border-0 mb-4'>
                  <div className='card-body'>
                    <h6 className='card-title fw-bold mb-3'>Account Status</h6>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Status</small>
                        <span
                          className={`badge ${
                            selectedUser.status === 'ACTIVE'
                              ? 'bg-success'
                              : 'bg-danger'
                          }`}
                        >
                          {selectedUser.status}
                        </span>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>KYC Status</small>
                        <span
                          className={`badge ${
                            selectedUser.kycStatus === 'APPROVED'
                              ? 'bg-success'
                              : selectedUser.kycStatus === 'PENDING'
                              ? 'bg-warning'
                              : 'bg-danger'
                          }`}
                        >
                          {selectedUser.kycStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KYC Reason Input */}
                {selectedUser.kycStatus === 'PENDING' && (
                  <div className='mb-4'>
                    <label className='form-label fw-semibold'>
                      Reason (Optional)
                    </label>
                    <textarea
                      className='form-control'
                      rows='3'
                      placeholder='Enter reason for approval or rejection...'
                      value={kycReason}
                      onChange={(e) => setKycReason(e.target.value)}
                    />
                  </div>
                )}

                {/* KYC Documents */}
                <div className='card border-primary'>
                  <div className='card-header bg-primary bg-opacity-10 border-primary'>
                    <h6 className='mb-0 fw-bold text-primary'>KYC Documents</h6>
                  </div>
                  <div className='card-body'>
                    <div className='row g-3'>
                      {selectedUser.idCardFront && (
                        <div className='col-md-6'>
                          <strong className='d-block mb-2'>
                            ID Card Front:
                          </strong>
                          <AuthenticatedMedia
                            src={selectedUser.idCardFront}
                            alt='ID Front'
                            className='img-fluid rounded border'
                            style={{
                              maxHeight: '200px',
                              width: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      )}
                      {selectedUser.idCardBack && (
                        <div className='col-md-6'>
                          <strong className='d-block mb-2'>
                            ID Card Back:
                          </strong>
                          <AuthenticatedMedia
                            src={selectedUser.idCardBack}
                            alt='ID Back'
                            className='img-fluid rounded border'
                            style={{
                              maxHeight: '200px',
                              width: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      )}
                      {selectedUser.selfieVideo && (
                        <div className='col-12'>
                          <strong className='d-block mb-2'>
                            Selfie Video:
                          </strong>
                          <AuthenticatedMedia
                            as='video'
                            src={selectedUser.selfieVideo}
                            controls
                            className='img-fluid rounded border'
                            style={{ maxHeight: '300px', width: '100%' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Edit Mode
              <div className='card border-primary'>
                <div className='card-header bg-primary bg-opacity-10 border-primary'>
                  <h6 className='mb-0 fw-bold text-primary'>
                    Edit Profile Information
                  </h6>
                </div>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-6'>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>
                          Full Name
                        </label>
                        <input
                          type='text'
                          className='form-control'
                          value={editedUser.fullName || ''}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              fullName: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>Email</label>
                        <input
                          type='email'
                          className='form-control'
                          value={editedUser.email}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              email: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>Phone</label>
                        <input
                          type='text'
                          className='form-control'
                          value={editedUser.phone || ''}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              phone: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className='col-md-6'>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>
                          Date of Birth
                        </label>
                        <input
                          type='date'
                          className='form-control'
                          value={editedUser.dateOfBirth || ''}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              dateOfBirth: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>Age</label>
                        <input
                          type='number'
                          className='form-control'
                          value={editedUser.age || ''}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              age: parseInt(e.target.value) || '',
                            })
                          }
                        />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>
                          Country
                        </label>
                        <input
                          type='text'
                          className='form-control'
                          value={editedUser.country || ''}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              country: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className='col-12'>
                      <div className='mb-0'>
                        <label className='form-label fw-semibold'>
                          Address
                        </label>
                        <textarea
                          className='form-control'
                          rows='3'
                          value={editedUser.address || ''}
                          onChange={(e) =>
                            setEditedUser({
                              ...editedUser,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='modal-footer border-top'>
            {!editMode ? (
              <>
                {selectedUser.kycStatus === 'PENDING' && (
                  <>
                    <button
                      className='btn btn-success'
                      onClick={approveKYC}
                      disabled={
                        loadingApprove || loadingReject || loadingSuspend
                      }
                    >
                      {loadingApprove ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          Processing...
                        </>
                      ) : (
                        'Approve KYC'
                      )}
                    </button>
                    <button
                      className='btn btn-warning'
                      onClick={rejectKYC}
                      disabled={
                        loadingApprove || loadingReject || loadingSuspend
                      }
                    >
                      {loadingReject ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          Processing...
                        </>
                      ) : (
                        'Reject KYC'
                      )}
                    </button>
                  </>
                )}
                <button
                  className='btn btn-primary'
                  onClick={() => onChangeLoginPassword(selectedUser)}
                  disabled={loadingApprove || loadingReject || loadingSuspend}
                >
                  View/Change Login Password
                </button>
                <button
                  className='btn btn-warning'
                  onClick={() => onOpenFundPassword(selectedUser)}
                  disabled={loadingApprove || loadingReject || loadingSuspend}
                >
                  View/Edit Fund Password
                </button>
                <button
                  className='btn btn-info'
                  onClick={openEditMode}
                  disabled={loadingApprove || loadingReject || loadingSuspend}
                >
                  Edit Profile
                </button>
                <button
                  className='btn btn-danger'
                  onClick={toggleBanStatus}
                  disabled={loadingApprove || loadingReject || loadingSuspend}
                >
                  {loadingSuspend ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Processing...
                    </>
                  ) : selectedUser.status === 'ACTIVE' ? (
                    'Suspend User'
                  ) : (
                    'Unsuspend User'
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  className='btn btn-secondary'
                  onClick={() => setEditMode(false)}
                  disabled={loadingSave}
                >
                  Cancel
                </button>
                <button
                  className='btn btn-primary'
                  onClick={saveUserProfile}
                  disabled={loadingSave || !hasChanges}
                >
                  {loadingSave ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageModal;
